import { useTranslation } from 'react-i18next';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { DashboardBookingCard } from 'us-react-components/dist/DashboardBookingCard/DashboardBookingCard';
import { DateFormat, formatDateFromString, sameDay } from 'us-react-components/dist/utils/date';
import { Booking } from 'us-web-services';
import { Routes } from '../../../../../Routes';
import { Link } from '../../../../Link';

type Props = {
  bookings: Booking[];
}

type LinkComponentProps = {
  booking: Booking;
}

const BookingLinkComponent = ({ booking }: LinkComponentProps): JSX.Element => {
  const { t } = useTranslation();

  const containsPaymentAction = (): boolean => {
    let result = false;

    booking?.status?.parentActions?.forEach(action => {
      if (action.code && action.code === 'parent_confirm_payment') {
        result = true;
      }
    });

    return result;
  }

  return containsPaymentAction() ?
    (<Link className='block py-1 my-1 font-bold text-sm' to={`/app${Routes.bookings.root}/${booking.id}/confirm-payment`} external>
      {t('dashboard.widgets.bookings.pay')} {booking?.sitter?.firstName}
    </Link>) :
    (<Link className='block py-1 my-1 font-bold text-sm' to={`/app${Routes.bookings.root}/${booking.id}`} external>
      {t('dashboard.widgets.bookings.viewDetails')}
    </Link>);
}

export const DashboardBookings = ({ bookings = [] }: Props): JSX.Element => {
  const { t } = useTranslation();

  const getDateString = (booking: Booking): string => {
    const start = new Date(booking?.start || '');
    const end = new Date(booking?.end || '');

    if (sameDay(start, end)) {
      return `${formatDateFromString(booking?.start || '', DateFormat.DATETIME_WEEKDAY_FORMAT)}
        - ${formatDateFromString(booking?.end || '', DateFormat.TIME_FORMAT)}`
    }

    return `${formatDateFromString(booking?.start || '', DateFormat.DATETIME_WEEKDAY_FORMAT)}
      - ${formatDateFromString(booking?.end || '', DateFormat.DATETIME_WEEKDAY_FORMAT)}`
  }

  return (
    <div className='px-4 place-content-center bg-neutral-100 rounded-2xl'>
      <div className='grid grid-cols-1 pt-2'>
        {bookings.map(booking => (
          <div className='pb-2 mt-2' key={`booking-${booking.id}`}>
            <DashboardBookingCard
              booking={booking}
              profile={booking.caregiver}
              getDateString={getDateString}
              linkComponent={<BookingLinkComponent booking={booking} />}
            />
          </div>
        ))}
        <div className='pb-2 mt-2'>
          <Link className='block py-1 my-1 text-sm' to={`/app${Routes.bookings.root}?filterType=all_date`} external>
            {t('dashboard.widgets.bookings.viewAll')}
            <FontAwesomeIcon icon={faAngleRight} className='pl-2 m-[-1px]' />
          </Link>
        </div>
      </div>
    </div>
  );
};
